import { ConfirmationResult } from "firebase/auth";
import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Primary } from "@ui/components/containers/primary";
import { PhoneNumberIcon } from "@ui/components/icons";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { ClosableModal } from "@ui/modals/closableModal";
import { theme } from "@ui/styles/theme";
import { createToast } from "@ui/toasts/utils";

interface ChangeNumberModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
}
export const ChangeNumberModal: React.FC<ChangeNumberModalProps> = ({ close, ...htmlProps }) => {
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const indicator = "221";
	const [code, setCode] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [confirmObj, setConfirmObj] = useState<ConfirmationResult | undefined>(undefined);
	// const [isRecaptchaChecked, setIsRecaptchaChecked] = useState<boolean>(false);
	const [isCodeResend, setIsCodeResend] = useState<boolean>(false);
	const { currentProfileService } = useServices();
	const currentProfile = useCurrentProfile();
	const history = useHistory();

	const { run: setupRecaptcha, loading: recaptchaLoading } = useAsync(
		async (phoneNumber: string, isRecaptchaDisplay?: boolean) => {
			try {
				const setup = await currentProfileService.setupRecaptcha(phoneNumber, isRecaptchaDisplay);
				if (setup) {
					setIsCodeResend(true);
					setConfirmObj(setup.confirm);
					setup.recaptcha.clear();
				}
			} catch (error) {
				console.log("error", error);
			}
		},
		[]
	);

	const { run: loginWithPhoneNumber } = useAsync(async (confirmObj: ConfirmationResult, code: string) => {
		try {
			setLoading(true);
			const result = await currentProfileService.checkUserWithPhoneNumber(confirmObj, code);
			console.log(result);
			if (!result) {
				close();
				return;
			}
			history.push(Path.LinkNumberAction);
		} catch (error) {
			console.log("error", error);
			createToast(I18n.t("error"), I18n.t("settings.link_phonenumber.toast.error.message"), "error");
		} finally {
			setLoading(false);
		}
	}, []);

	const isPhoneNumber = phoneNumber.length !== 0;
	const isCodeInvalid = code.length === 0;

	const [seconds, setSeconds] = useState(30);
	const [count, setCount] = useState<number>(5);
	useEffect(() => {
		const interval = setInterval(() => {
			if (seconds > 0 && isCodeResend) {
				setSeconds(seconds - 1);
			}

			if (seconds === 0) {
				setIsCodeResend(false);
				clearInterval(interval);
			}
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [seconds, isCodeResend]);

	const currentPhoneNumber = currentProfile?.phoneNumberAuth?.substring(indicator.length + 1);

	return (
		<Container close={close} {...htmlProps}>
			{!confirmObj ? (
				<>
					<ContainerLabel>
						<Primary>
							<b>{I18n.t("settings.modal.update_phonenumber.title")}</b>
						</Primary>
					</ContainerLabel>
					<FormContainer
						onSubmit={async (e) => {
							e.preventDefault();
							if (!phoneNumber) {
								return;
							}
							if (phoneNumber === currentPhoneNumber) {
								setupRecaptcha("+".concat("", indicator).concat("", phoneNumber));
								createToast(I18n.t("success"), I18n.t("settings.link_phonenumber.toast.success.message"), "success");
							} else {
								createToast(I18n.t("error"), I18n.t("settings.link_phonenumber.toast.error.message"), "error");
							}
						}}
					>
						<SimpleTextInput
							color="primary"
							icon={<PhoneNumberIcon color={theme.colors.primary} />}
							iconPosition="left"
							value={phoneNumber}
							onChange={(value) => {
								setPhoneNumber(value);
							}}
							placeholder={I18n.t("register.phonenumber.placeholder")}
						/>
						<div id="recaptcha-container" />
						<ContainerBtn>
							<LoadingButton loading={recaptchaLoading} disabled={!isPhoneNumber} type="submit">
								{"Confirmer"}
							</LoadingButton>
						</ContainerBtn>
					</FormContainer>
				</>
			) : (
				<>
					<ContainerLabel>
						<Primary>
							<b>{I18n.t("settings.link_phonenumber.otp.label")}</b>
						</Primary>
					</ContainerLabel>
					<FormContainer
						onSubmit={async (value) => {
							value.preventDefault();
							if (!code) {
								return;
							}
							loginWithPhoneNumber(confirmObj, code);
							//    history.push(Path.LinkNumberAction)
						}}
					>
						<SimpleTextInput value={code} onChange={(value) => setCode(value)} />
						<div id="recaptcha-container" />
						<ContainerBtn>
							<LoadingButton loading={loading} disabled={isCodeInvalid} type="submit">
								Valider
							</LoadingButton>
						</ContainerBtn>
						<Label>
							{I18n.t("code.message")}
							{isCodeResend ? (
								<Counter>00 : {seconds}</Counter>
							) : (
								<StyledTertiaryButton
									disabled={count === 0}
									onClick={() => {
										setSeconds(15);
										setCount((current) => current - 1);
										setConfirmObj(undefined);
										// setupRecaptcha("+".concat("", indicator).concat("", phoneNumber), false);
									}}
								>
									{I18n.t("code.resend")}
								</StyledTertiaryButton>
							)}
						</Label>
					</FormContainer>
				</>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	padding: 60px;
`;

const FormContainer = styled.form`
	margin-top: 10px;
	.input-wrapper > svg .main-path {
		fill: ${({ theme }) => theme.colors.primary};
	}
`;
const ContainerBtn = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 30px;
`;
const ContainerLabel = styled.div`
	display: flex;
`;
const StyledTertiaryButton = styled(TertiaryButton)`
	color: ${({ theme }) => theme.colors.secondary};
`;

const Counter = styled.div`
	color: ${({ theme }) => theme.colors.secondary};
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;
`;
const Label = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;

	color: rgba(0, 0, 0, 0.5);
`;
